import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarCheck, VideoCamera, Notebook, SignOut } from "@phosphor-icons/react";

export default function DoctorPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [apts, setApts] = useState([]);
  const [active, setActive] = useState(null);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("completed");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/doctor/appointments").then((r) => setApts(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/doctor/appointments/${active.id}/notes`, { clinical_notes: notes, status });
      toast.success("Notes saved");
      setActive(null);
      load();
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const openApt = (a) => {
    setActive(a);
    setNotes(a.clinical_notes || "");
    setStatus(a.status || "completed");
  };

  const upcoming = apts.filter((a) => new Date(a.slot_iso) >= new Date(Date.now() - 3600000));
  const past = apts.filter((a) => new Date(a.slot_iso) < new Date(Date.now() - 3600000));

  return (
    <div className="min-h-screen bg-bone flex">
      <aside className="hidden md:flex w-60 flex-col bg-ayurveda text-bone">
        <div className="p-6 border-b border-white/10">
          <div className="font-heritage text-2xl">Gutvibe</div>
          <div className="overline text-terracotta text-[10px] mt-1">Doctor Portal</div>
        </div>
        <nav className="p-3 flex-1 space-y-1">
          <Link to="/doctor" className="block px-4 py-2 rounded-md bg-white/10 text-sm">Appointments</Link>
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-sm mb-2">{user?.name}</div>
          <Button size="sm" onClick={() => { logout(); nav("/"); }} className="w-full bg-white/10 hover:bg-white/20 text-white border-0" data-testid="doctor-logout">
            <SignOut size={14} className="mr-1" /> Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 p-6 md:p-10">
        <div className="mb-8">
          <div className="overline text-terracotta">Your practice</div>
          <h1 className="font-clinical text-4xl text-charcoal mt-1">Appointments</h1>
        </div>

        <div className="grid md:grid-cols-3 gap-3 mb-8">
          <Card className="bg-white border-black/5 rounded-md"><CardContent className="p-4"><div className="overline text-charcoal/50 text-[10px]">Total</div><div className="font-clinical text-3xl">{apts.length}</div></CardContent></Card>
          <Card className="bg-white border-black/5 rounded-md"><CardContent className="p-4"><div className="overline text-charcoal/50 text-[10px]">Upcoming</div><div className="font-clinical text-3xl text-ayurveda">{upcoming.length}</div></CardContent></Card>
          <Card className="bg-white border-black/5 rounded-md"><CardContent className="p-4"><div className="overline text-charcoal/50 text-[10px]">Completed</div><div className="font-clinical text-3xl text-terracotta">{past.length}</div></CardContent></Card>
        </div>

        <div className="space-y-6">
          <div>
            <div className="overline text-charcoal/50 mb-3">Upcoming</div>
            {upcoming.length === 0 && <div className="text-charcoal/50 text-sm">No upcoming appointments.</div>}
            <div className="space-y-2">
              {upcoming.map((a) => (
                <Card key={a.id} className="border-black/5 bg-white rounded-md" data-testid={`dr-apt-${a.id}`}>
                  <CardContent className="p-4 flex items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className="bg-ayurveda text-bone">{a.specialty}</Badge>
                        <Badge className={a.payment_status === "paid" ? "bg-green-700 text-white" : "bg-terracotta text-white"}>{a.payment_status}</Badge>
                      </div>
                      <div className="font-clinical text-charcoal">{a.patient_name}</div>
                      <div className="text-xs text-charcoal/60">{new Date(a.slot_iso).toLocaleString("en-IN")}</div>
                      {a.notes && <div className="text-xs text-charcoal/60 mt-1 italic">Patient: "{a.notes}"</div>}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => openApt(a)} data-testid={`dr-notes-${a.id}`}><Notebook size={14} className="mr-1"/> Notes</Button>
                      <Button size="sm" className="bg-ayurveda text-bone" onClick={() => nav(`/video/${a.id}`)} data-testid={`dr-join-${a.id}`}>
                        <VideoCamera size={14} className="mr-1" /> Join
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div>
            <div className="overline text-charcoal/50 mb-3">Past consultations</div>
            <div className="space-y-2">
              {past.map((a) => (
                <Card key={a.id} className="border-black/5 bg-white rounded-md">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <div className="font-clinical text-charcoal">{a.patient_name}</div>
                      <div className="text-xs text-charcoal/60">{new Date(a.slot_iso).toLocaleString("en-IN")} · {a.status}</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => openApt(a)}><Notebook size={14} className="mr-1" /> {a.clinical_notes ? "View notes" : "Add notes"}</Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
          <DialogContent className="bg-white max-w-lg">
            <DialogHeader><DialogTitle className="font-clinical text-2xl">Clinical notes — {active?.patient_name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <div className="overline text-charcoal/50 text-[10px] mb-1">Status</div>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="dr-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="in_progress">In progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="overline text-charcoal/50 text-[10px] mb-1">Notes</div>
                <Textarea rows={8} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Chief complaint, examination, diagnosis, plan…" data-testid="dr-notes-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setActive(null)}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-ayurveda text-bone" data-testid="dr-notes-save">{saving ? "Saving…" : "Save"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
